import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, Input } from 'reactstrap';
import withLoader from 'components/organisms/withLoader';
import { API_MEDICAL_HISTORY } from 'services/dataService/ccmResources';
import Pagination from 'components/molecules/paginationTransition';
import { showResult } from 'utils';
import useTable from 'utils/useTable';
import useValidation from 'utils/useValidation';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import classNames from 'classnames';
import TableActions from '../components/tableActions';
import EditMedicalHistoryModal from './modal';

const validateAndSaveChanges =
  (validateRow, saveData, reloader) => async () => {
    const result = await validateRow();
    if (result.isValid) {
      const { error } = await saveData();
      if (error) showResult(error);
      else showResult();
      reloader();
    }
  };
const VALIDATION_RULES = Yup.object().shape({
  issue: Yup.string().nullable(),
});
const PER_PAGE = 5;
const createRow = () => ({
  issue: '',
  surgeryHist: '',
  hospitalizationHist: '',
});

const MedicalHistory = ({ canEditPage }) => {
  const { patientGuid } = useParams();
  const [isRowSaved, setIsRowSaved] = useState(false);
  const [medicalHistoryPopupOpen, setMedicalHistoryPopupOpen] = useState(false);
  const [medicalHistoryToUpdate, setMedicalHistoryToUpdate] = useState({});

  const { row, setRow, isValid, validate, messageErrors } = useValidation(
    {},
    VALIDATION_RULES,
  );
  const clearRows = () => {
    setIsRowSaved(true);
    setRow({});
  };
  const createNewRow = () => {
    setIsRowSaved(false);
    setRow(createRow());
  };
  const updateMedicalHistoryModal = (data) => {
    setMedicalHistoryToUpdate(data);
    setMedicalHistoryPopupOpen(true);
  };
  const {
    data: { items },
    count,
    loading,
    page,
    setPage,
    saveNewRow,
    updateRow,
    setFilters,
  } = useTable(
    API_MEDICAL_HISTORY,
    PER_PAGE,
    {
      patientGuid,
    },
    {
      makeInitialRequest: false,
    },
  );

  const disableSaveButton = useMemo(
    () =>
      !Object.values(row).some(
        (value) =>
          value?.trim() !== '' && value !== null && value !== undefined,
      ),
    [row],
  );

  const submitUpdate = async (postData) => {
    setMedicalHistoryPopupOpen(false);
    const { error, data } = await updateRow(API_MEDICAL_HISTORY, {
      ...postData,
      patientGuid,
    });

    return { error, data };
  };

  useEffect(() => {
    let isMounted = false;

    if (!isMounted) {
      setFilters({ patientGuid });
      setRow({});
      setIsRowSaved(true);
      clearRows();
    }

    return () => {
      isMounted = true;
    };
  }, [patientGuid]);

  return (
    <>
      <Card className="card-border ccm-table-section">
        <CardBody>
          <h3 className="ccm-table-section__h3">Past Medical History</h3>
          {withLoader(
            loading,
            <>
              <div className="table-outer table-custom-scrollbar">
                <table cellSpacing="0" cellPadding="0" className="table-border">
                  <thead>
                    <tr>
                      <th>Medical Issue</th>
                      <th>Surgery History</th>
                      <th>Hospitalization History</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!!items.length &&
                      items.map((el, index) => (
                        <tr
                          onClick={() => updateMedicalHistoryModal(el)}
                          key={index}
                        >
                          <td>{el.issue}</td>
                          <td>{el.surgeryHist}</td>
                          <td>{el.hospitalizationHist}</td>
                        </tr>
                      ))}

                    {!isRowSaved && (
                      <>
                        <tr className="create-medical-history">
                          <td
                            className={classNames({
                              'table-column__input': true,
                              'table-column__input--error':
                                !row.issue && !isValid,
                            })}
                          >
                            <div>
                              <div className="table-column__input-container">
                                <Input
                                  plaintext
                                  bsSize="sm"
                                  className="table-input input--text-plain"
                                  defaultValue={row.issue}
                                  onChange={(e) =>
                                    setRow({
                                      ...row,
                                      issue: e.target.value,
                                    })
                                  }
                                />
                                {row.issue && (
                                  <div className="table-column__character-length">
                                    Character Count: {row.issue.length}
                                  </div>
                                )}
                              </div>
                              {messageErrors.issue ? (
                                <div className="table-column__error">
                                  {messageErrors.issue}
                                </div>
                              ) : null}
                            </div>
                          </td>
                          <td
                            className={classNames({
                              'table-column__input': true,
                            })}
                          >
                            <div>
                              <div className="table-column__input-container">
                                <Input
                                  plaintext
                                  bsSize="sm"
                                  defaultValue={row.surgeryHist}
                                  onChange={(e) =>
                                    setRow({
                                      ...row,
                                      surgeryHist: e.target.value,
                                    })
                                  }
                                />
                                {row.surgeryHist && (
                                  <div className="table-column__character-length">
                                    Character Count: {row.surgeryHist.length}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td
                            className={classNames({
                              'table-column__input': true,
                            })}
                          >
                            <div>
                              <div className="table-column__input-container">
                                <Input
                                  plaintext
                                  bsSize="sm"
                                  defaultValue={row.hospitalizationHist}
                                  onChange={(e) =>
                                    setRow({
                                      ...row,
                                      hospitalizationHist: e.target.value,
                                    })
                                  }
                                />
                                {row.hospitalizationHist && (
                                  <div className="table-column__character-length">
                                    Character Count:{' '}
                                    {row.hospitalizationHist.length}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          {/* TODO: fix <p> tag can't be nested in <tr> tag */}
                          {disableSaveButton && (
                            <p>at least one field required*</p>
                          )}
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
              <TableActions
                save={validateAndSaveChanges(
                  validate,
                  () =>
                    saveNewRow(API_MEDICAL_HISTORY, {
                      ...row,
                      patientGuid,
                    }),
                  clearRows,
                )}
                cancel={clearRows}
                new={createNewRow}
                rowIsSaved={isRowSaved}
                disabled={disableSaveButton}
              />

              <div className="ccm-table-section__pagination">
                <Pagination
                  title=""
                  page={page}
                  pageTotal={items.length}
                  itemsPerPage={PER_PAGE}
                  total={count}
                  handleFirstClick={() => setPage(1)}
                  handleLastClick={() => setPage(Math.ceil(count / PER_PAGE))}
                  handlePrevClick={() => setPage(page - 1)}
                  handleNextClick={() => setPage(page + 1)}
                  handleGoToPage={(numberPage) => setPage(numberPage)}
                />
              </div>
            </>,
          )}
        </CardBody>
      </Card>
      {canEditPage && (
        <EditMedicalHistoryModal
          isOpen={medicalHistoryPopupOpen}
          toggle={setMedicalHistoryPopupOpen}
          patientGuid={patientGuid}
          loading={loading}
          submit={submitUpdate}
          medicalHistory={medicalHistoryToUpdate}
        />
      )}
    </>
  );
};

MedicalHistory.propTypes = {
  canEditPage: PropTypes.bool,
};

export default MedicalHistory;
